import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../common/types';
import { SMLSettingsTabDef, SMLSettingsTabId } from '../tabs/types';
import { settingsCommonMessages } from '../tabs/settingsCommonMessages';
import { Panel } from '../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import styles from './SMLSettingsViewRenderer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  tabs: SMLSettingsTabDef[];
  activeTab: SMLSettingsTabId;
  /** A red count on a tab (Connections: the accounts that need a new sign-in). */
  badges?: Partial<Record<SMLSettingsTabId, number>>;
  onSelect: (tabId: SMLSettingsTabId) => void;
}

/** The desktop tab rail, with a line between two blocks (design settings-redesign a1). */
const SettingsRailComponent: React.FC<Props> = ({ tabs, activeTab, badges, onSelect }) => {
  const intl = useIntl();
  return (
    <Panel as="nav" className={styles.tabRail} aria-label={intl.formatMessage(settingsCommonMessages.railLabel)}>
      {tabs.map((tab, index) => {
        const isActive = tab.id === activeTab;
        const startsBlock = index > 0 && tabs[index - 1]?.block !== tab.block;
        const badge = badges?.[tab.id] ?? 0;
        return (
          <React.Fragment key={tab.id}>
            {startsBlock && <div className={styles.separator} role="separator" />}
            <button
              type="button"
              className={`${styles.tabButton}${isActive ? ` ${styles.tabButtonActive}` : ''}`}
              onClick={() => onSelect(tab.id)}
              aria-current={isActive ? 'page' : undefined}
            >
              <FontAwesomeIcon icon={tab.icon} className={styles.icon} fixedWidth />
              <span>{intl.formatMessage(tab.label)}</span>
              {badge > 0 && (
                <span className={styles.tabBadge} data-testid={`settings-tab-badge-${tab.id}`}>
                  {intl.formatNumber(badge)}
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </Panel>
  );
};

export const SettingsRail = TypedMemo(SettingsRailComponent);
